import { useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { FiTag } from 'react-icons/fi';

import { useAdminApi } from '../Admin/hooks/useAdminApi';
import { useToast } from '../../../hooks/toast';
import apiBackoffice from '../../../services/apiBackoffice';
import { CouponsModels } from '../../../models/CouponsModels';

import Button from '../../../components/Atoms/Button';
import { Modal } from '../../../components/Atoms/Modal';
import {
  PageHeader,
  EmptyState,
  ErrorState,
  SkeletonTable,
  Confirm,
  SearchField,
  useAdminResource,
} from '../../../components/Admin';

import { ListContainer } from '../../_layout/AdminLayout.styles';
import CouponsTable from './components/CouponsTable';
import AddCoupon from './components/AddCoupon';
import EditCoupon from './components/EditCoupon';
import { useCourseTitles } from './hooks/useCourseTitles';

const AdminCoupon = () => {
  const { fetchCoupons } = useAdminApi();
  const { addToast } = useToast();
  const courseTitles = useCourseTitles();

  const { data, setData, isLoading, loadError, reload } = useAdminResource<
    CouponsModels,
    unknown
  >({
    fetchPage: () => fetchCoupons(),
    select: response => (Array.isArray(response) ? response : []),
    errorMessage: 'Não foi possível buscar a lista de cupons.',
  });

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editing, setEditing] = useState<CouponsModels | null>(null);
  const [pendingCoupon, setPendingCoupon] = useState<CouponsModels | null>(null);
  const [deletingId, setDeletingId] = useState('');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase();
    if (!q) return data;
    return data.filter(coupon => {
      const product = courseTitles[coupon.productid] ?? coupon.productid;
      return (
        coupon.couponid.toLocaleLowerCase().includes(q) ||
        product.toLocaleLowerCase().includes(q)
      );
    });
  }, [data, search, courseTitles]);

  const handleModalState = () => setOpenModal(prevState => !prevState);

  // Mesma chamada do antigo RemoveCoupon. O corpo sem `schoolid` é um bug
  // conhecido do backend de cupons, registrado e mantido de propósito.
  const confirmDelete = async () => {
    const coupon = pendingCoupon;
    setPendingCoupon(null);
    if (!coupon) return;

    setDeletingId(coupon.couponid);
    try {
      const response = await apiBackoffice.post<string>('backoffice/coupons/delete', {
        couponid: coupon.couponid,
      });

      if (response.data !== 'OK') throw new Error('O servidor não confirmou a exclusão.');

      setData(items => items.filter(item => item.couponid !== coupon.couponid));
      addToast({ type: 'success', title: 'Cupom excluído' });
    } catch {
      addToast({
        type: 'error',
        title: 'Não foi possível excluir o cupom',
        description: 'O servidor não confirmou a exclusão. Tente novamente.',
      });
    } finally {
      setDeletingId('');
    }
  };

  const showInitialLoading = isLoading && data.length === 0 && !loadError;

  return (
    <>
      <PageHeader
        title="Cupons"
        subtitle="Códigos de desconto aplicados na compra de cursos."
        actions={
          <Button onClick={handleModalState}>
            <FaPlus />
            Novo Cupom
          </Button>
        }
      />

      <ListContainer>
        {showInitialLoading ? (
          <SkeletonTable rows={5} columns={5} />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={reload} />
        ) : data.length === 0 ? (
          <EmptyState
            icon={<FiTag size={40} />}
            title="Nenhum cupom cadastrado"
            description="Crie um cupom para oferecer desconto em um curso."
            action={
              <Button onClick={handleModalState}>
                <FaPlus />
                Criar cupom
              </Button>
            }
          />
        ) : (
          <>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Buscar por código ou curso..."
              label="Buscar cupom por código ou curso"
              count={`${filtered.length} de ${data.length} cupons`}
            />
            {filtered.length === 0 ? (
              <EmptyState
                icon={<FiTag size={40} />}
                title="Nenhum cupom encontrado"
                description="Tente outro código ou nome de curso."
              />
            ) : (
              <CouponsTable
                data={filtered}
                courseTitles={courseTitles}
                deletingId={deletingId}
                onEdit={setEditing}
                onDelete={setPendingCoupon}
              />
            )}
          </>
        )}
      </ListContainer>

      <Modal isOpen={openModal}>
        <AddCoupon onClose={handleModalState} refreshFeth={reload} />
      </Modal>

      <Modal isOpen={editing !== null}>
        {editing && (
          <EditCoupon dataItem={editing} onClose={() => setEditing(null)} refreshFeth={reload} />
        )}
      </Modal>

      <Confirm
        open={pendingCoupon !== null}
        danger
        title="Excluir cupom"
        description={`O cupom "${pendingCoupon?.couponid}" deixa de valer na hora. Não dá para desfazer.`}
        confirmLabel="Excluir"
        loading={!!deletingId}
        onConfirm={confirmDelete}
        onCancel={() => setPendingCoupon(null)}
      />
    </>
  );
};

export default AdminCoupon;
